import { Outlet, useParams } from 'react-router';
import { Globe } from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  StatusBadge,
  DateCell,
  NotificationCard,
  Card,
  type StatusTone,
} from '@sighthost/ui';
import { useDomain } from '@/domains/domains-registry';
import { relativeExpiry } from '@/shared/utils/format';
import type { DomainStatus } from '@/domains/domains-registry';

const STATUS_LABEL: Record<DomainStatus, string> = {
  active: 'Active',
  expiring: 'Expiring',
  expired: 'Expired',
  pending: 'Pending',
  'transfer-in': 'Transfer in',
  'transfer-out': 'Transfer out',
  suspended: 'Suspended',
};

const STATUS_TONE: Record<DomainStatus, StatusTone> = {
  active: 'active',
  expiring: 'expiring',
  expired: 'overdue',
  pending: 'pending',
  'transfer-in': 'info',
  'transfer-out': 'info',
  suspended: 'failed',
};

export default function DomainDetailLayout() {
  const { domain } = useParams();
  const decoded = domain ? decodeURIComponent(domain) : undefined;
  const { data, isLoading, error } = useDomain(decoded);

  const baseBreadcrumbs = [
    { label: 'Home', href: '/app' },
    { label: 'Domains', href: '/app/domains' },
  ];

  if (isLoading) {
    return (
      <UniversalPage
        title={decoded ?? 'Domain'}
        breadcrumbs={[...baseBreadcrumbs, { label: decoded ?? 'Detail' }]}
      >
        <UniversalLoadingState variant="spinner" caption="Loading domain…" />
      </UniversalPage>
    );
  }

  if (error) {
    return (
      <UniversalPage
        title="Could not load domain"
        breadcrumbs={[...baseBreadcrumbs, { label: decoded ?? 'Detail' }]}
      >
        <NotificationCard tone="danger" title="Could not load domain" body={error.message} />
      </UniversalPage>
    );
  }

  if (!data) {
    return (
      <UniversalPage
        title="Domain not found"
        breadcrumbs={[...baseBreadcrumbs, { label: decoded ?? 'Detail' }]}
      >
        <Card interactive={false} className="p-12 text-center text-sm text-muted-foreground">
          We could not find a domain called <code className="rounded-sm bg-muted px-1">{decoded}</code> on your account.
        </Card>
      </UniversalPage>
    );
  }

  return (
    <UniversalPage
      title={
        <span className="inline-flex items-center gap-2">
          <Globe className="h-5 w-5 text-muted-foreground" />
          {data.name}
        </span>
      }
      breadcrumbs={[...baseBreadcrumbs, { label: data.name }]}
      meta={
        <>
          <StatusBadge tone={STATUS_TONE[data.status]}>
            {STATUS_LABEL[data.status]}
          </StatusBadge>
          <span className="text-muted-foreground">·</span>
          <span className="inline-flex items-center gap-1">
            Expires <DateCell value={data.expiresAt} format="long" /> ({relativeExpiry(data.expiresAt)})
          </span>
          <span className="text-muted-foreground">·</span>
          <span>
            Auto-renew: <strong className="font-medium text-foreground">{data.autoRenew ? 'On' : 'Off'}</strong>
          </span>
        </>
      }
      tabs={[
        { label: 'Overview',    to: '.',           end: true },
        { label: 'Contacts',    to: 'contacts' },
        { label: 'Nameservers', to: 'nameservers' },
        { label: 'DNS records', to: 'dns' },
        { label: 'Transfer',    to: 'transfer' },
      ]}
      tabsRenderOutlet={false}
    >
      <Outlet context={data} />
    </UniversalPage>
  );
}
